package com.tencent.kuikly.core.render.web.expand.module

import com.tencent.kuikly.core.render.web.export.KuiklyRenderBaseModule
import com.tencent.kuikly.core.render.web.ktx.KuiklyRenderCallback
import com.tencent.kuikly.core.render.web.ktx.kuiklyWindow
import com.tencent.kuikly.core.render.web.ktx.toJSONObjectSafely
import com.tencent.kuikly.core.render.web.nvi.serialization.json.JSONObject
import com.tencent.kuikly.core.render.web.utils.Log
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.fetch.CORS
import org.w3c.fetch.Headers
import org.w3c.fetch.INCLUDE
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.fetch.RequestMode
import org.w3c.fetch.Response
import kotlin.js.Promise
import kotlin.js.json

/** Kuikly network request module */
class KRNetworkModule : KuiklyRenderBaseModule() {
    override fun call(method: String, params: String?, callback: KuiklyRenderCallback?): Any? {
        return when (method) {
            METHOD_HTTP_REQUEST -> httpRequest(params, callback)
            else -> super.call(method, params, callback)
        }
    }

    override fun call(method: String, params: Any?, callback: KuiklyRenderCallback?): Any? {
        return when (method) {
            METHOD_HTTP_STREAM_REQUEST -> httpStreamRequest(params, callback)
            else -> super.call(method, params, callback)
        }
    }

    /**
     * Initiate Web host HTTP request
     */
    private fun httpRequest(params: String?, callback: KuiklyRenderCallback?) {
        // Get HTTP request parameters
        val paramsJSON = params.toJSONObjectSafely()
        var url = paramsJSON.optString("url")
        val method = paramsJSON.optString("method")
        val param = paramsJSON.optJSONObject("param")
        val header = paramsJSON.optJSONObject("headers")
        val cookie = paramsJSON.optString("cookie")
        // Request timeout duration, original unit is seconds
        val timeout = paramsJSON.optInt("timeout") * 1000
        // HTTP response status code
        var httpCode = 0
        // HTTP response header information
        var httpHeaders = ""
        // Get the actual request url link
        url = getRequestUrl(method, url, param)
        Log.trace("httpRequestParams", param.toString(), getPostParams(param))

        // Timeout judgment
        val requestTimeoutPromise = Promise<Any> { _, reject ->
            // Directly throw an exception after timeout
            kuiklyWindow.setTimeout({
                reject(Error("request timeout"))
            }, timeout)
        }
        // Use window.fetch to initiate network request
        val fetchPromise = kuiklyWindow.fetch(
            url, RequestInit(
                // Request method
                method = method,
                // Include all cookies
                credentials = RequestCredentials.INCLUDE,
                // Request header information
                headers = getRequestHeaders(header, cookie),
                // Request data, non-POST request passes null
                body = if (method == HTTP_METHOD_POST) getPostParams(param) else null,
                // Request mode is cross-domain mode
                mode = RequestMode.CORS
            ).also {
                // Attach the timeout via a non-standard field so that MiniGlobal.fetch in the
                // mini program runtime can forward it to wx.request. Browsers ignore unknown
                // RequestInit fields, so this is safe for the H5 environment as well.
                if (timeout > 0) {
                    it.asDynamic()["timeout"] = timeout
                }
            }
        )
        // Timeout and response, whoever comes first is processed first
        Promise.race(arrayOf(requestTimeoutPromise, fetchPromise.unsafeCast<Promise<*>>()))
            .then { rsp ->
                // Since timeout won't go to the then logic, this is definitely a Response object
                val response = rsp.unsafeCast<Response>()

                // Save the status code of this response
                httpCode = response.status.toInt()
                // Save headers
                httpHeaders = JSON.stringify(response.headers)
                // Read as text first. Browser Response.json() rejects for an
                // empty body, and non-2xx JSON bodies contain useful business
                // details that must be forwarded to shared code.
                response.text()
            }
            .then { data ->
                val resultData = data?.toString().orEmpty().ifBlank { "{}" }
                if (httpCode in 200..299) {
                    // Normal response
                    fireSuccessCallback(callback, resultData, httpHeaders, httpCode)
                } else {
                    // Preserve the error response body for business-level
                    // status handling such as 401/409/422.
                    fireErrorCallback(callback, "request error", httpCode, resultData)
                }
            }
            .catch { error ->
                val errorMsg = error.message ?: "Unknown error"
                val errorType = determineErrorType(error, errorMsg)
                Log.error("HTTP request failed: $errorType - $errorMsg")
                
                // Exception or error occurred, callback with detailed error info
                fireErrorCallback(
                    callback,
                    "$errorType: $errorMsg",
                    when {
                        httpCode != 0 -> httpCode
                        errorType == ERROR_TYPE_TIMEOUT -> STATE_CODE_TIMEOUT
                        errorType == ERROR_TYPE_NETWORK -> STATE_CODE_NETWORK_ERROR
                        else -> STATE_CODE_UNKNOWN
                    }
                )
            }
    }
    
    /**
     * Determine the type of error based on the error object and message
     */
    private fun determineErrorType(error: dynamic, errorMsg: String): String {
        return when {
            errorMsg.contains("timeout", ignoreCase = true) -> ERROR_TYPE_TIMEOUT
            errorMsg.contains("network", ignoreCase = true) -> ERROR_TYPE_NETWORK
            errorMsg.contains("abort", ignoreCase = true) -> ERROR_TYPE_ABORTED
            error.name == "TypeError" -> ERROR_TYPE_NETWORK
            error.name == "AbortError" -> ERROR_TYPE_ABORTED
            else -> ERROR_TYPE_UNKNOWN
        }
    }

    /**
     * Initiate Web host HTTP streaming request
     */
    private fun httpStreamRequest(params: Any?, callback: KuiklyRenderCallback?) {
        // Get http request related parameters
        val dataArray = params.unsafeCast<Array<*>>()

        val body = dataArray[1].unsafeCast<ByteArray>()
        val paramsString = dataArray[0].unsafeCast<String>()
        val paramsArray = JSONObject(paramsString)
        val url = paramsArray.optString("url")
        val headerStr = paramsArray.optString("headers")
        val cookie = paramsArray.optString("cookie")
        var method = paramsArray.optString("method")
        val timeout = paramsArray.optInt("timeout") * 1000

        Log.trace("httpStreamRequest", url ?: "", body, headerStr, cookie, timeout)

        // HTTP response status code
        var httpCode = 0
        // HTTP response header information
        var httpHeaders = ""

        // Timeout judgment
        val requestTimeoutPromise = Promise<Any> { _, reject ->
            // Directly throw an exception after timeout
            kuiklyWindow.setTimeout({
                reject(Error("httpStreamRequest timeout"))
            }, timeout)
        }
        // Use window.fetch to initiate network request
        val fetchPromise = kuiklyWindow.fetch(
            url, RequestInit(
                // Request method
                method = method,
                // Include all cookies
                credentials = RequestCredentials.INCLUDE,
                // Request header information
                headers = getRequestHeaders(if (headerStr.isNullOrEmpty()) null else JSONObject(headerStr), cookie),
                // Request data, non-POST request passes null
                body = body.toBlob(),
                // Request mode is cross-domain mode
                mode = RequestMode.CORS
            ).also {
                // Attach the timeout via a non-standard field so that MiniGlobal.fetch in the
                // mini program runtime can forward it to wx.request. Browsers ignore unknown
                // RequestInit fields, so this is safe for the H5 environment as well.
                if (timeout > 0) {
                    it.asDynamic()["timeout"] = timeout
                }
            }
        )
        // Timeout and response, whoever comes first is processed first
        Promise.race(arrayOf(requestTimeoutPromise, fetchPromise.unsafeCast<Promise<*>>()))
            .then { rsp ->
                // Since timeout won't go to the then logic, this is definitely a Response object
                val response = rsp.unsafeCast<Response>()

                // Save the status code of this response
                httpCode = response.status.toInt()

                // Save headers
                httpHeaders = JSON.stringify(response.headers)
                if (!response.ok) {
                    // Response is not normal, return empty content
                    ArrayBuffer(0)
                } else {
                    response.arrayBuffer()
                }
            }
            .then { data ->
                if (httpCode in 200..299) {
                    // Normal response
                    fireStreamRequestResultCallback(
                        callback,
                        data.unsafeCast<ArrayBuffer>(),
                        httpHeaders,
                        "",
                        httpCode
                    )
                } else {
                    // Exception or error occurred, callback
                    fireStreamRequestResultCallback(
                        callback,
                        ArrayBuffer(0),
                        httpHeaders,
                        "request error",
                        httpCode
                    )
                }
            }
            .catch { error ->
                val errorMsg = error.message ?: "Unknown error"
                val errorType = determineErrorType(error, errorMsg)
                Log.error("HTTP stream request failed: $errorType - $errorMsg")
                
                // Exception or error occurred, callback with detailed error info
                fireStreamRequestResultCallback(
                    callback,
                    ArrayBuffer(0),
                    httpHeaders,
                    "$errorType: $errorMsg",
                    when {
                        httpCode != 0 -> httpCode
                        errorType == ERROR_TYPE_TIMEOUT -> STATE_CODE_TIMEOUT
                        errorType == ERROR_TYPE_NETWORK -> STATE_CODE_NETWORK_ERROR
                        else -> STATE_CODE_UNKNOWN
                    }
                )
            }
    }

    private fun ByteArray.toBlob(): ArrayBuffer {
        val buffer = ArrayBuffer(size)
        val uint8Array = Uint8Array(buffer)
        forEachIndexed { index, byte ->
            uint8Array[index] = byte
        }
        return buffer
    }

    private fun fireStreamRequestResultCallback(
        callback: KuiklyRenderCallback?,
        arrayBuffer: ArrayBuffer,
        headers: String,
        errorMsg: String,
        statusCode: Int,
    ) {
        val info = JSONObject().apply {
            put("headers", headers)
            put("errorMsg", errorMsg)
            put("statusCode", statusCode)
            put("success", 1)
        }
        callback?.invoke(
            arrayOf(
                info.toString(),
                arrayBuffer.toByteArray(),
            )
        )
    }

    private fun ArrayBuffer.toByteArray(): ByteArray {
        val int8Array = Uint8Array(this)
        return ByteArray(int8Array.length) { index ->
            int8Array[index]
        }
    }

    /**
     * Get the actual request url link
     */
    private fun getRequestUrl(method: String, url: String, params: JSONObject?): String {
        // If it's a GET request, parameters need to be appended to the url link
        if (method == HTTP_METHOD_GET) {
            var reqParams = ""
            if (params != null) {
                val urlParams = arrayListOf<String>()
                val keys = params.keys()
                for (key in keys) {
                    val v = params.opt(key)?.toString() ?: ""
                    urlParams.add("$key=${kuiklyWindow.asDynamic().encodeURIComponent(v)}")
                }
                reqParams = urlParams.joinToString("&")
            }

            // Append query parameters to the link
            return if (url.contains("?")) {
                "$url&$reqParams"
            } else {
                "$url?$reqParams"
            }
        } else {
            return url
        }
    }


    /**
     * Normal callback
     */
    private fun fireSuccessCallback(
        callback: KuiklyRenderCallback?,
        resultData: String,
        headers: String,
        statusCode: Int
    ) {
        callback?.invoke(
            mapOf(
                "data" to resultData,
                KEY_SUCCESS to 1,
                KEY_ERROR_MSG to "",
                KEY_HEADERS to headers,
                KEY_STATUS_CODE to statusCode
            )
        )
    }

    /**
     * Exception callback
     */
    private fun fireErrorCallback(
        callback: KuiklyRenderCallback?,
        errorMsg: String,
        statusCode: Int,
        resultData: String = "",
    ) {
        callback?.invoke(
            mapOf(
                "data" to resultData,
                KEY_SUCCESS to 0,
                KEY_ERROR_MSG to errorMsg,
                KEY_STATUS_CODE to statusCode
            )
        )
    }

    /**
     * Get post parameters
     */
    private fun getPostParams(param: JSONObject?): String {
        if (param == null) {
            return ""
        }
        return param.toString()
    }

    /**
     * Get custom header
     */
    private fun getRequestHeaders(header: JSONObject?, cookie: String? = null): Headers {
        val headers = json()
        // Process request header
        if (header != null) {
            val headerKeys = header.keys()
            for (key in headerKeys) {
                // Get the input header value
                val headerString = header.opt(key)
                headers[key] = if (headerString === null) {
                    // Empty value
                    ""
                } else if (headerString is String) {
                    // Original value
                    headerString
                } else {
                    // JSONObject, convert to String
                    headerString.toString()
                }
            }
        }

        if (cookie != null) {
            headers["Cookie"] = cookie
        }
        headers["Content-Type"] = "application/json"
        return Headers(headers)
    }

    companion object {
        const val MODULE_NAME = "KRNetworkModule"
        private const val METHOD_HTTP_REQUEST = "httpRequest"
        private const val METHOD_HTTP_STREAM_REQUEST = "httpRequestBinary"
        // Network request success
        private const val KEY_SUCCESS = "success"
        // Network request failure
        private const val KEY_ERROR_MSG = "errorMsg"
        // Network request header field name
        private const val KEY_HEADERS = "headers"
        // Network request status code field
        private const val KEY_STATUS_CODE = "statusCode"
        // GET request method
        private const val HTTP_METHOD_GET = "GET"
        // POST request method
        private const val HTTP_METHOD_POST = "POST"
        // Unknown status code
        private const val STATE_CODE_UNKNOWN = -1000
        // Timeout status code
        private const val STATE_CODE_TIMEOUT = -1001
        // Network error status code
        private const val STATE_CODE_NETWORK_ERROR = -1002
        // HTTP success status code range
        private val HTTP_SUCCESS_RANGE = 200..299
        
        // Error type constants
        private const val ERROR_TYPE_TIMEOUT = "Timeout"
        private const val ERROR_TYPE_NETWORK = "NetworkError"
        private const val ERROR_TYPE_ABORTED = "Aborted"
        private const val ERROR_TYPE_UNKNOWN = "UnknownError"
    }
}
